import { create } from "zustand"

import type { PlanUpgradePayload } from "@/api/fair-use"

/**
 * Fair-use UI state shared between React components and the non-React WS
 * routing layer (fair-use-ws-bridge.ts): a structured `fair_use_blocked`
 * error calls `useFairUseStore.getState().openPopover()` so the composer
 * indicator auto-opens its popover with the request-more flow.
 *
 * The request-more dialog's open flag also lives here (not inside the popover
 * content) so the dialog survives the popover closing — clicking into a modal
 * dialog counts as an outside interaction that dismisses the popover, which
 * would otherwise unmount dialog state held there.
 */
type FairUseStore = {
  popoverOpen: boolean
  requestDialogOpen: boolean
  /**
   * The most recent `plan_upgrade_required` refusal (fair use round 2).
   *
   * The chat error card is handed a bare `errorCode` string and has no route
   * to the frame's `feature`/`plan`/`required_plans`, so the refusal lands
   * here on its way through `routeFairUseError` and the card reads it back.
   * Null until a plan refusal happens; a later one replaces it.
   */
  planUpgrade: PlanUpgradePayload | null
  setPopoverOpen: (open: boolean) => void
  openPopover: () => void
  setRequestDialogOpen: (open: boolean) => void
  /** Close the popover and open the request-more dialog in one transition. */
  openRequestDialog: () => void
  setPlanUpgrade: (payload: PlanUpgradePayload | null) => void
}

export const useFairUseStore = create<FairUseStore>()((set) => ({
  popoverOpen: false,
  requestDialogOpen: false,
  planUpgrade: null,
  setPopoverOpen: (open) => set({ popoverOpen: open }),
  openPopover: () => set({ popoverOpen: true }),
  setRequestDialogOpen: (open) => set({ requestDialogOpen: open }),
  openRequestDialog: () => set({ popoverOpen: false, requestDialogOpen: true }),
  setPlanUpgrade: (payload) => set({ planUpgrade: payload }),
}))
